import { useEffect, useMemo, useState } from 'preact/hooks'
import { FieldLabel } from './FieldLabel'
import Icon from 'snippets/icon/icon'
import { formatDisplayDates } from '../helpers/parseBirthday'

const MONTH_KEYS = {
  NEXT_MONTH: 'next_month',
  PREVIOUS_MONTH: 'prev_month'
}
const DAY_DATE_REFERENCE = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTH_REFERENCE = [
  { month: 'January', dateIndex: 0 },
  { month: 'February', dateIndex: 1 },
  { month: 'March', dateIndex: 2 },
  { month: 'April', dateIndex: 3 },
  { month: 'May', dateIndex: 4 },
  { month: 'June', dateIndex: 5 },
  { month: 'July', dateIndex: 6 },
  { month: 'August', dateIndex: 7 },
  { month: 'September', dateIndex: 8 },
  { month: 'October', dateIndex: 9 },
  { month: 'November', dateIndex: 10 },
  { month: 'December', dateIndex: 11 }
]
const YEAR_RANGE = 150
const CURRENT_YEAR = new Date().getFullYear()
const YEAR_REFERENCE = [CURRENT_YEAR, ...Array.from(new Array(YEAR_RANGE)).map((_, i) => CURRENT_YEAR - (i + 1))]

const parseValueToDate = (value, showDay = true) => {
  const now = new Date()
  if (!value) return now

  const parts = value.split('-')
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1

  if (showDay && parts.length === 3) {
    const day = parseInt(parts[2], 10)
    if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
      return new Date(year, month, day)
    }
  } else if (!showDay && parts.length >= 2) {
    if (!isNaN(year) && !isNaN(month)) {
      return new Date(year, month, 1)
    }
  }

  return now
}

const getPreviousMonth = (month) => {
  const previousMonth = month - 1
  if (previousMonth < 0) {
    return 11
  }
  return previousMonth
}

function getMonthDayRange(month, year) {
  const previousMonth = getPreviousMonth(month + 1)
  const currentDate = new Date(year, month + 1, 0)
  const previousMonthDate = new Date(year, month, 1)
  const firstDayOfMonth = previousMonthDate.getDay()
  const daysInMonth = currentDate.getDate()

  const previousMonthsDays = Array.from(new Array(firstDayOfMonth))
  const days = Array.from(new Array(daysInMonth)).map((_, i) => i + 1)

  return {
    startDate: firstDayOfMonth,
    maxDays: daysInMonth,
    days: [...previousMonthsDays, ...days]
  }
}

const handleNextMonthEvent = (role, value) => {
  switch (role) {
    case MONTH_KEYS.NEXT_MONTH: {
      const nextMonth = value + 1
      return nextMonth > 11 ? 0 : nextMonth
    }
    case MONTH_KEYS.PREVIOUS_MONTH: {
      const prevMonth = value - 1
      return prevMonth < 0 ? 11 : prevMonth
    }
    default: {
      return value
    }
  }
}

const handleYear = (role, month, year) => {
  switch (role) {
    case MONTH_KEYS.NEXT_MONTH: {
      const nextMonth = month + 1
      return nextMonth > 11 ? year + 1 : year
    }
    case MONTH_KEYS.PREVIOUS_MONTH: {
      const nextMonth = month - 1
      return nextMonth < 0 ? year - 1 : year
    }
    default: {
      return year
    }
  }
}

const DatePicker = ({ callback, name, handleView, view, value, showDay = true }) => {
  const [date, setDate] = useState(() => {
    const initialDate = parseValueToDate(value, showDay)
    return {
      now: initialDate,
      month: initialDate.getMonth(),
      year: initialDate.getFullYear(),
      selectedDate: initialDate
    }
  })

  const [isFirstRender, setIsFirstRender] = useState(true)
  const handleBelowZero = (value) => {
    return value < 10 ? `0${value}` : value
  }

  useEffect(() => {
    if (!isFirstRender && !!value) {
      const currentSelectedDate = parseValueToDate(value, showDay)
      const newDate = {
        now: currentSelectedDate,
        month: currentSelectedDate.getMonth(),
        year: currentSelectedDate.getFullYear(),
        selectedDate: currentSelectedDate
      }
      setDate((c) => ({ ...c, ...newDate }))
    }
    return () => {
      setIsFirstRender(false)
    }
  }, [view, value, showDay])

  const handleCallback = () => {
    let formattedValue
    if (showDay) {
      formattedValue = `${date.selectedDate.getFullYear()}-${handleBelowZero(
        date.selectedDate.getMonth() + 1
      )}-${handleBelowZero(date.selectedDate.getDate())}`
    } else {
      formattedValue = `${date.year}-${handleBelowZero(date.month + 1)}`
    }

    // eslint-disable-next-line n/no-callback-literal
    callback({
      currentTarget: {
        name,
        value: formattedValue
      }
    })
  }

  const handleNavigation = (e) => {
    const element = e.currentTarget
    const { role } = element.dataset

    if (showDay) {
      if (
        role === MONTH_KEYS.NEXT_MONTH &&
        YEAR_REFERENCE[0] === date.year &&
        MONTH_REFERENCE[MONTH_REFERENCE.length - 1].dateIndex === date.month
      ) {
        return
      }

      const newMonth = handleNextMonthEvent(role, date.month)
      const newYear = handleYear(role, date.month, date.year)

      const newNow = new Date(date.now)
      newNow.setFullYear(newYear)
      newNow.setMonth(newMonth)
      newNow.setDate(1)

      const newDate = {
        now: newNow,
        month: newNow.getMonth(),
        year: newNow.getFullYear()
      }
      setDate((c) => ({ ...c, ...newDate }))
    } else {
      // When showDay is false, navigation arrows change the year
      const newYear = role === MONTH_KEYS.NEXT_MONTH ? date.year + 1 : date.year - 1
      const newNow = new Date(date.now)
      newNow.setFullYear(newYear)
      newNow.setMonth(date.month)
      newNow.setDate(1)

      const newDate = {
        now: newNow,
        month: newNow.getMonth(),
        year: newNow.getFullYear()
      }
      setDate((c) => ({ ...c, ...newDate }))
    }
  }

  const dateConfig = useMemo(() => {
    const obj = getMonthDayRange(date.month, date.year)
    return obj
  }, [date.month, date.year])

  if (!view) return <></>

  return (
    <div className={'rounded-md border border-solid border-primary-black bg-primary-white select-none'}>
      <div className={'p-4 pt-0'}>
        <div data-role="header" className={'flex flex-row justify-between gap-2 py-4'}>
          <div onClick={handleNavigation} className={'my-auto cursor-pointer'} data-role={MONTH_KEYS.PREVIOUS_MONTH}>
            <Icon id="chevron-left" />
          </div>
          <div className={'flex flex-row gap-2'}>
            {showDay && (
              <div>
                <select
                  onChange={(e) => {
                    const { value } = e.currentTarget
                    const now = new Date(date.now)
                    now.setMonth(value)
                    setDate((c) => ({ ...c, now, month: now.getMonth(), year: now.getFullYear() }))
                  }}
                  className={'border-none'}
                >
                  {MONTH_REFERENCE.map((month) => {
                    const selectedMonth = date.month === month.dateIndex
                    return (
                      <option value={month.dateIndex} selected={selectedMonth}>
                        {month.month}
                      </option>
                    )
                  })}
                </select>
              </div>
            )}
            <div>
              <select
                onChange={(e) => {
                  const { value } = e.currentTarget
                  const now = new Date(date.now)
                  now.setFullYear(value)
                  setDate((c) => ({ ...c, now, month: now.getMonth(), year: now.getFullYear() }))
                }}
                className={'border-none'}
              >
                {YEAR_REFERENCE.map((year) => {
                  const selectedYear = date.year === year
                  return (
                    <option selected={selectedYear} value={year}>
                      {year}
                    </option>
                  )
                })}
              </select>
            </div>
          </div>
          <div className={'my-auto cursor-pointer'} onClick={handleNavigation} data-role={MONTH_KEYS.NEXT_MONTH}>
            <Icon id="chevron-right" />
          </div>
        </div>
        {showDay ? (
          <div data-role="body">
            <div data-role="header" className={'grid grid-cols-7 justify-between gap-2'}>
              {DAY_DATE_REFERENCE.map((day) => (
                <div className={'h-12 text-center font-bold'}>{day}</div>
              ))}
            </div>
            <div className={'grid grid-cols-7 justify-between gap-2'}>
              {dateConfig.days.map((day) => {
                const selectedDay =
                  date.selectedDate.getDate() === day &&
                  date.month === date.selectedDate.getMonth() &&
                  date.year === date.selectedDate.getFullYear()
                return (
                  <div
                    onClick={() => {
                      const now = new Date(date.now)
                      now.setFullYear(date.year)
                      now.setMonth(date.month)
                      now.setDate(day)
                      const newDate = {
                        selectedDate: now
                      }
                      setDate((c) => ({ ...c, ...newDate }))
                    }}
                    className={`m-auto flex aspect-1 h-9 w-9 cursor-pointer items-center justify-center rounded-full text-center transition-colors hover:bg-slate-50 md:h-11 md:w-11 ${
                      selectedDay && 'bg-primary-black text-primary-white hover:bg-primary-black'
                    }`}
                  >
                    {day}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          <div data-role="body" className="grid grid-cols-3 gap-4 p-4">
            {MONTH_REFERENCE.map((month) => {
              const selectedMonth = date.month === month.dateIndex
              return (
                <div
                  onClick={() => {
                    const now = new Date(date.now)
                    now.setMonth(month.dateIndex)
                    setDate((c) => ({ ...c, now, month: now.getMonth(), year: now.getFullYear() }))
                  }}
                  className={`flex h-12 cursor-pointer items-center justify-center rounded-lg text-center transition-colors hover:bg-slate-50 ${
                    selectedMonth ? 'bg-primary-black text-primary-white' : ''
                  }`}
                >
                  {month.month}
                </div>
              )
            })}
          </div>
        )}
        <div data-role="footer" className={'flex flex-row justify-between py-4'}>
          <div class={'w-1/2'}>
            <button
              onClick={() => {
                if (!value) return
                const originalDate = parseValueToDate(value, showDay)
                setDate({
                  now: originalDate,
                  month: originalDate.getMonth(),
                  year: originalDate.getFullYear(),
                  selectedDate: originalDate
                })
                handleView()
              }}
              type="button"
              className="m-auto btn btn-sm btn-outline-black"
            >
              Close
            </button>
          </div>
          <div class={'w-1/2'}>
            <button
              onClick={() => {
                handleCallback()
                handleView()
              }}
              type="button"
              className="m-auto btn btn-sm btn-solid-black"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const FieldDate = ({ name, required, value, label, onChange, withPicker = true, showDay = true }) => {
  const [isFocused, setIsFocused] = useState(false)
  const [view, setView] = useState(false)
  const handleView = () => setView((v) => !v)

  const displayValue = formatDisplayDates(value, showDay)

  return (
    <div className={'flex w-full flex-col gap-2'}>
      <FieldLabel {...{ label, hasInput: !!value, required }} />
      <div
        className={`relative overflow-hidden rounded-lg border border-solid border-neutrals-tertiary-grey bg-primary-white transition-colors
        ${isFocused || !!value ? 'border-2 border-secondary-green' : 'border-neutrals-tertiary-grey'}`}
        onClick={withPicker ? handleView : undefined}
      >
        {withPicker && (
          <div
            className={`absolute bottom-0 right-0 top-0 flex h-full w-12 items-center justify-center bg-primary-white ${
              withPicker ? 'pointer-events-none' : ''
            }`}
          >
            <Icon id="calendar" />
          </div>
        )}
        <input
          onFocus={() => {
            if (!withPicker) setIsFocused(true)
          }}
          onBlur={() => {
            if (!withPicker) setIsFocused(false)
          }}
          className={`w-full border-none px-4 ${withPicker ? 'pointer-events-none' : ''}`}
          type={withPicker ? 'text' : showDay ? 'date' : 'month'}
          placeholder={showDay ? 'dd/mm/yyyy' : 'MM/YYYY'}
          readOnly={!!withPicker}
          disabled={false}
          name={name}
          required={required}
          value={displayValue}
          onChange={!withPicker ? onChange : undefined}
        />
      </div>
      {withPicker && <DatePicker callback={onChange} name={name} handleView={handleView} view={view} value={value} showDay={showDay} />}
    </div>
  )
}
